import { useState } from "react";
import { Link } from "react-router-dom";
import useCreateBooking, { BOOKING_RESULT } from "../../hooks/useCreateBooking";
import useTurnstile from "../../hooks/useTurnstile";
import { CountryCodeSelect, Label } from "../../components/Inputs";

// CON-002 §11 CreateBookingRequest constraints.
const MAX_NAME = 150;
const MAX_EMAIL = 254;
// Phone is validated by E.164 digit count (≤15, CON-002 amended). The <input>
// maxLength is a generous char guard that allows separators/spaces while the
// digit count stays authoritative.
const MAX_PHONE_DIGITS = 15;
const MAX_PHONE_INPUT = 20;
const MAX_EVENT_TYPE = 100;
const MAX_NOTES = 1000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Today's date (local) as YYYY-MM-DD, for the date input's min attribute. */
const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

/** Labeled input with inline error (a11y: <label> wired via htmlFor/id). */
function Field({
  id, label, type = "text", value, onChange, error, maxLength,
  autoComplete, inputMode, placeholder, min, required, as = "input",
}) {
  const sharedClass =
    "w-full border-2 border-charcoal rounded-xl px-3 py-2 font-body text-ink bg-white focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal";
  return (
    <div className="mb-4">
      <Label
        label={label}
        required={required}
        htmlFor={id}
        className="block font-heading tracking-widest text-teal-deep text-sm mb-1"
      />
      {as === "textarea" ? (
        <textarea
          id={id}
          name={id}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          placeholder={placeholder}
          required={required}
          aria-required={required || undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          data-testid={`field-${id}`}
          className={sharedClass}
        />
      ) : (
        <input
          id={id}
          name={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          min={min}
          autoComplete={autoComplete}
          inputMode={inputMode}
          placeholder={placeholder}
          required={required}
          aria-required={required || undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          data-testid={`field-${id}`}
          className={sharedClass}
        />
      )}
      {error && (
        <p id={`${id}-error`} role="alert" data-testid={`error-${id}`} className="text-rust text-xs mt-1 font-body">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Book — private-event / appearance booking request (F-014, CON-002 §5A).
 *
 * Email-only: POSTs to /api/bookings; the server validates and emails the request
 * to the TCSB inbox (nothing is persisted). Styled per BLU-006 (bordered cards,
 * Bebas teal labels, rust primary button).
 *  - 202 → success confirmation panel.
 *  - 422 → inline field errors from ProblemDetails.errors.
 *  - 502 / network → retryable banner.
 */
export default function Book() {
  const { submitBooking, submitting } = useCreateBooking();
  const { containerRef: captchaRef, token: captchaToken, reset: resetCaptcha } = useTurnstile();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [headcount, setHeadcount] = useState("");
  const [notes, setNotes] = useState("");

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  /** Phone with the selected dial code prepended (e.g. "+1 612-555-0100"). */
  const combinedPhone = () => {
    const local = phone.trim();
    if (!local) return "";
    return phoneCountry ? `${phoneCountry} ${local}` : local;
  };

  /** Client-side validation mirroring CON-002 §5A limits. */
  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Name is required.";
    else if (name.length > MAX_NAME) errs.name = `Name must be ${MAX_NAME} characters or fewer.`;

    if (!email.trim()) errs.email = "Email is required.";
    else if (email.length > MAX_EMAIL) errs.email = `Email must be ${MAX_EMAIL} characters or fewer.`;
    else if (!EMAIL_RE.test(email)) errs.email = "Enter a valid email address.";

    // E.164: count digits across the combined <dial><number> value; the rule is
    // ≤15 digits (CON-002 amended). Separators/spaces are ignored.
    const phoneDigits = combinedPhone().replace(/\D/g, "");
    if (phoneDigits.length > MAX_PHONE_DIGITS) errs.phone = "Phone number must be 15 digits or fewer.";

    if (!eventType.trim()) errs.eventType = "Event type is required.";
    else if (eventType.length > MAX_EVENT_TYPE) errs.eventType = `Event type must be ${MAX_EVENT_TYPE} characters or fewer.`;

    if (!eventDate) errs.eventDate = "Event date is required.";
    else if (eventDate < todayIso()) errs.eventDate = "Event date must not be in the past.";

    const headcountNum = Number(headcount);
    if (!headcount) errs.headcount = "Headcount is required.";
    else if (!Number.isInteger(headcountNum) || headcountNum < 1) errs.headcount = "Headcount must be at least 1.";

    if (notes && notes.length > MAX_NOTES) errs.notes = `Notes must be ${MAX_NOTES} characters or fewer.`;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFormError(null);

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Captcha must be solved before we send (CON-002 §5A). The token is verified
    // server-side; a missing/invalid one → 422.
    if (!captchaToken) {
      setFormError("Please complete the verification challenge before sending.");
      return;
    }

    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: combinedPhone() || undefined,
      eventType: eventType.trim(),
      eventDate,
      headcount: Number(headcount),
      notes: notes.trim() || undefined,
      captchaToken,
    };

    const res = await submitBooking(payload);

    if (res.result === BOOKING_RESULT.SUCCESS) {
      setSubmitted(true);
      return;
    }
    if (res.result === BOOKING_RESULT.VALIDATION) {
      // A failed/missing captcha comes back as a 422 (CON-002 §5A) — reset the
      // widget so the user gets a fresh challenge token to retry with.
      resetCaptcha();
      setFieldErrors(res.fieldErrors);
      setFormError(res.detail);
      return;
    }
    // EMAIL (502) / NETWORK / ERROR — retryable banner.
    setFormError(res.detail);
  };

  if (submitted) {
    return (
      <main data-testid="book-page" className="min-h-screen bg-cream text-charcoal font-body">
        <section className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="book-success">
          <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">REQUEST SENT</p>
          <h1 className="font-display text-5xl text-charcoal uppercase mb-4">Thanks!</h1>
          <p className="font-body text-ink/70 text-lg mb-8">
            We&apos;ve received your booking request and emailed it to our team. We&apos;ll be in
            touch soon to lock in the details.
          </p>
          <Link
            to="/"
            data-testid="book-back-home"
            className="inline-block bg-rust text-white font-heading tracking-widest px-8 py-3 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          >
            BACK HOME
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="book-page" className="min-h-screen bg-cream text-charcoal font-body">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">BOOK THE BULLET</p>
        <h1 className="font-display text-5xl text-charcoal uppercase mb-2">Private Events</h1>
        <p className="font-body text-ink/70 mb-8">
          Request the Silver Bullet for your wedding, corporate event, or private party. Tell us a
          little about your event and we&apos;ll get back to you.
        </p>

        {formError && (
          <div data-testid="book-error" role="alert" className="mb-6 border-2 border-rust bg-rust/10 text-rust rounded-xl px-4 py-3 font-body text-sm">
            {formError}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          noValidate
          data-testid="book-form"
          className="bg-white border-2 border-charcoal rounded-2xl shadow-[4px_4px_0_rgba(43,39,36,0.12)] p-5 sm:p-6"
        >
          <Field id="name" label="Name" value={name} onChange={setName} error={fieldErrors.name} maxLength={MAX_NAME} autoComplete="name" required />
          <Field id="email" label="Email" type="email" value={email} onChange={setEmail} error={fieldErrors.email} maxLength={MAX_EMAIL} autoComplete="email" inputMode="email" required />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="mb-4">
              <CountryCodeSelect name="phone-country" value={phoneCountry} onChange={setPhoneCountry} />
            </div>
            <div className="sm:col-span-2">
              <Field id="phone" label="Phone" type="tel" value={phone} onChange={setPhone} error={fieldErrors.phone} maxLength={MAX_PHONE_INPUT} autoComplete="tel" inputMode="tel" />
            </div>
          </div>
          <Field id="eventType" label="Event Type" value={eventType} onChange={setEventType} error={fieldErrors.eventType} maxLength={MAX_EVENT_TYPE} placeholder="Wedding, corporate, birthday…" required />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="eventDate" label="Event Date" type="date" value={eventDate} onChange={setEventDate} error={fieldErrors.eventDate} min={todayIso()} required />
            <Field id="headcount" label="Headcount" type="number" value={headcount} onChange={setHeadcount} error={fieldErrors.headcount} min="1" inputMode="numeric" placeholder="50" required />
          </div>

          <Field id="notes" label="Notes" as="textarea" value={notes} onChange={setNotes} error={fieldErrors.notes} maxLength={MAX_NOTES} placeholder="Location, timing, special requests…" />

          {/* Cloudflare Turnstile — bot check. The widget renders into this div
              (useTurnstile loads the script lazily); the resulting token is sent
              as captchaToken and verified server-side (CON-002 §5A). */}
          <div className="mb-4" ref={captchaRef} data-testid="captcha-widget" />

          <button
            type="submit"
            disabled={submitting}
            data-testid="book-submit-button"
            className="block w-full bg-rust text-white font-heading tracking-widest text-lg py-3 rounded-xl text-center shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] active:translate-y-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-[0_4px_0_#7f371e]"
          >
            {submitting ? "SENDING…" : "SEND REQUEST"}
          </button>
        </form>
      </div>
    </main>
  );
}
